import React, { useEffect, useState, useRef } from "react";
import { bool, string, shape, arrayOf } from "prop-types";
import { connect, useDispatch } from "react-redux";
import { Col } from "react-bootstrap";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import * as i18nUtils from "util/i18n";
import { goBack, push } from "connected-react-router";
import { selectors as onboardingSelectors, actions as onboardingActions } from "reducers/onboarding";
import { selectors as enrollmentSelectors } from "reducers/enrollment";
import { actions as modalActions } from "reducers/modal";
import { isIOS } from "react-device-detect";

import Checkbox from "pages/_components/Checkbox";
import { DateRangeField } from "pages/_components/fields/DateRangeField";
import Head from "pages/_components/Head";
import MainContainer from "pages/_components/MainContainer";
import TextField from "pages/_components/fields/TextField";
import Button from "pages/_components/Button";
import I18n from "pages/_components/I18n";
import Selector from "pages/_components/fields/formik/Selector";
import CustomText from "pages/_components/CustomText/CustomText";
import { MobileButtons, ResponsiveHeader } from "pages/_components/header";
import { validateCUILPF } from "util/form";
import { getLimitDate } from "util/date";
import ErrorPopup from "../_components/ErrorPopup";
import Stepper from "../_components/Stepper";
import { helpStepPersonalData } from "./help";
import Help from "../_components/Help";

const PAGE_ID = "StepPersonalData";
const FORM_ID = "onboarding.step.personalData";

const StepPersonalData = ({
    isMobile,
    companyRoleList,
    maritalStatusList,
    occupationList,
    countryList,
    foreignCountries,
    stateList,
    localityList,
    address,
    personalDataStatus,
    onboardingWithInvitation,
    invitationRoles,
    exchangeToken,
    fetching,
    documentData,
    documentType,
    pepJobsList,
    pepRelationshipTypesList,
    compliance,
}) => {
    const dispatch = useDispatch();
    const [error, setError] = useState("");
    const [lastSelectedProvince, setLastSelectedProvince] = useState("");
    const [hasCuil, setHasCuil] = useState(false);
    const isLoading =
        !companyRoleList[0] ||
        !maritalStatusList[0] ||
        !occupationList[0] ||
        !countryList[0] ||
        !stateList[0] ||
        !pepJobsList[0] ||
        !pepRelationshipTypesList[0];
    const formRef = useRef(null);

    useEffect(() => {
        dispatch(onboardingActions.listRegulatoryInfoRequest());
    }, []);

    useEffect(() => {
        dispatch(onboardingActions.listPersonalInfoRequest(onboardingWithInvitation));
    }, [onboardingWithInvitation]);

    useEffect(() => {
        if (personalDataStatus && personalDataStatus === "SUCCESS") {
            dispatch(onboardingActions.resetPersonalDataStatus());
            dispatch(push("/onboarding/stepRegulatoryDataSuccess"));
        } else if (personalDataStatus?.length && personalDataStatus !== "FETCHING") {
            setError(personalDataStatus);
            dispatch(modalActions.modalShow());
        }
    }, [personalDataStatus]);

    useEffect(() => {
        if (documentData && documentData.cuil) {
            setHasCuil(true);
        }
    }, [documentData]);

    const onChangeProvince = (selectedProvince) => {
        if (!!selectedProvince && lastSelectedProvince !== selectedProvince) {
            const stateId = stateList.filter((state) => state.value === selectedProvince)[0].codeId || "";
            formRef.current.setFieldValue("locality", "");
            setLastSelectedProvince(selectedProvince);
            dispatch(onboardingActions.listLocalityRequest("1", Number(stateId)));
        }
    };

    const submitIsAllowed = ({ values }) => {
        const {
            companyRole,
            maritalStatus,
            occupation,
            country,
            province,
            locality,
            street,
            streetNumber,
            // No Required: "floor", "apartment"
            postalCode,
            // Datos adicionales
            fatca,
            crsOrOcde,
            pep,
            fatcaNumber1,
            fatcaNumber2,
            fatcaNumber3,
            isLocalResident,
            localResidentNumber,
            residentCountry,
            foreignResidentNumber,
            isPosition,
            positionType,
            positionRange,
            relationType,
        } = values;

        if (!!fatca && (!fatcaNumber1 || !fatcaNumber2 || !fatcaNumber3)) {
            return false;
        }
        if (crsOrOcde) {
            if (isLocalResident === "true" && localResidentNumber.length !== 11) {
                return false;
            }
            if (isLocalResident === "false" && (!residentCountry || !foreignResidentNumber)) {
                return false;
            }
        }
        if (pep) {
            if (isPosition === "true" && (!positionType || !positionRange[0] || !positionRange[1])) {
                return false;
            }
            if (isPosition === "false" && !relationType) {
                return false;
            }
        }

        const roleValid = onboardingWithInvitation ? true : !!companyRole;

        const firstFieldsAreValid = !!maritalStatus && !!occupation;

        const secondFieldsAreValid =
            !!country && !!province && !!locality && !!street && !!streetNumber && !!postalCode;
        return roleValid && firstFieldsAreValid && secondFieldsAreValid;
    };

    // const cleanAddressFieldsTouched = (allFormik) => {
    //     allFormik.setFieldTouched("province", false, false);
    //     allFormik.setFieldTouched("locality", false, false);
    //     allFormik.setFieldTouched("street", false, false);
    //     allFormik.setFieldTouched("streetNumber", false, false);
    //     allFormik.setFieldTouched("floor", false, false);
    //     allFormik.setFieldTouched("apartment", false, false);
    // };

    const onHeaderBack = () => {
        dispatch(goBack());
    };

    const requiredString = i18nUtils.get("forms.inputFile.massivePayments.invalid.required");

    const numberTest = (maxLength) =>
        Yup.string()
            .required(requiredString)
            .test(
                "format-number",
                i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                (value) =>
                    !Number.isNaN(Number(value)) &&
                    !value?.includes(" ") &&
                    (maxLength ? value?.length === maxLength : true),
            );

    const formikProps = {
        isInitialValid: onboardingWithInvitation ? compliance !== null : false,
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            companyRole: "",
            maritalStatus: "",
            occupation: "",
            country: "1",
            province: "",
            locality: "",
            street: "",
            streetNumber: "",
            floor: "",
            apartment: "",
            postalCode: "",
            cuil: "",
            // Datos adicionales
            documentData,
            documentType,
            fatca: (compliance !== null && compliance?.hasFatca) || false,
            fatcaNumber1: compliance !== null && !!compliance?.SSN ? compliance.SSN.split("-")[0] : "",
            fatcaNumber2: compliance !== null && !!compliance?.SSN ? compliance.SSN.split("-")[1] : "",
            fatcaNumber3: compliance !== null && !!compliance?.SSN ? compliance.SSN.split("-")[2] : "",

            crsOrOcde: (compliance !== null && compliance?.hasCRS) || false,
            isLocalResident:
                compliance !== null && compliance?.hasCRS && compliance?.crsLocal !== "" ? "true" : "false",
            localResidentNumber: compliance !== null && compliance?.hasCRS ? compliance?.crsLocal : "",
            residentCountry: "",
            foreignResidentNumber: compliance !== null && compliance?.hasCRS ? compliance?.crsAbroad : "",

            pep: (compliance !== null && compliance?.hasPEP) || false,
            isPosition: compliance !== null && !!compliance?.pepPosition ? "true" : "false",
            positionType: compliance !== null && !!compliance?.pepPosition ? compliance.pepPosition : "",
            positionRange:
                compliance?.hasPEP && compliance?.pepPosition
                    ? [
                          compliance?.dateFromPep ? new Date(compliance.dateFromPep) : null,
                          compliance?.dateToPep ? new Date(compliance.dateToPep) : null,
                      ]
                    : [null, null],

            relationType: compliance !== null && !!compliance?.pepRelation ? compliance.pepRelation : "",
        },
        initialErrors: { companyRole: requiredString },
        validationSchema: () =>
            Yup.object().shape({
                cuil: !hasCuil
                    ? Yup.string()
                          .required(i18nUtils.get("global.error.required"))
                          .test(
                              "cuil",
                              i18nUtils.get(
                                  "onboarding.step.personalData.cuil.error.invalidFormat",
                                  "Formato de CUIL inválido",
                              ),
                              (value) => validateCUILPF(value) && value.includes(documentData.documentNumber),
                          )
                    : Yup.string().notRequired(),
                companyRole: !onboardingWithInvitation
                    ? Yup.string().required(requiredString)
                    : Yup.string().notRequired(),
                maritalStatus: Yup.string().required(requiredString),
                occupation: Yup.string().required(requiredString),
                country: Yup.string().required(requiredString),
                province: Yup.string().required(requiredString),
                locality: Yup.string().required(requiredString),
                street: Yup.string().required(requiredString),
                streetNumber: Yup.string()
                    .required(requiredString)
                    .test(
                        "format-streetNumber",
                        i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                        (value) => !Number.isNaN(Number(value)),
                    ),
                floor: Yup.string().test(
                    "format-streetNumber",
                    i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                    (value) => !Number.isNaN(Number(value || 0)),
                ),
                apartment: Yup.string(),
                postalCode: Yup.string().required(requiredString),
                // Datos adicionales
                fatca: Yup.bool().required(requiredString),
                fatcaNumber1: Yup.string().when("fatca", { is: true, then: numberTest(3) }),
                fatcaNumber2: Yup.string().when("fatca", { is: true, then: numberTest(2) }),
                fatcaNumber3: Yup.string().when("fatca", { is: true, then: numberTest(4) }),

                crsOrOcde: Yup.bool().required(requiredString),
                isLocalResident: Yup.string().when("crsOrOcde", {
                    is: true,
                    then: Yup.string().required(requiredString),
                }),
                localResidentNumber: Yup.string().when(
                    ["crsOrOcde", "isLocalResident"],
                    (crsOrOcde, isLocalResident, schema) => {
                        if (!!crsOrOcde && isLocalResident === "true") {
                            return numberTest(11);
                        }
                        return schema;
                    },
                ),
                residentCountry: Yup.string().when(
                    ["crsOrOcde", "isLocalResident"],
                    (crsOrOcde, isLocalResident, schema) => {
                        if (!!crsOrOcde && isLocalResident === "false") {
                            return schema.required(requiredString);
                        }
                        return schema;
                    },
                ),
                foreignResidentNumber: Yup.string().when(
                    ["crsOrOcde", "isLocalResident"],
                    (crsOrOcde, isLocalResident, schema) => {
                        if (!!crsOrOcde && isLocalResident === "false") {
                            return Yup.string().required(requiredString);
                        }
                        return schema;
                    },
                ),
                pep: Yup.bool().required(requiredString),
                isPosition: Yup.string().when("pep", {
                    is: true,
                    then: Yup.string().required(requiredString),
                }),
                positionType: Yup.string().when(["pep", "isPosition"], (pep, isPosition, schema) => {
                    if (!!pep && isPosition === "true") {
                        return schema.required(requiredString);
                    }
                    return schema;
                }),
                positionRange: Yup.array()
                    .of(Yup.date().nullable())
                    .when(["pep", "isPosition"], (pep, isPosition, schema) => {
                        if (!!pep && isPosition === "true") {
                            return schema.required(requiredString);
                        }
                        return schema;
                    }),
                relationType: Yup.string().when(["pep", "isPosition"], (pep, isPosition, schema) => {
                    if (!!pep && isPosition === "false") {
                        return schema.required(requiredString);
                    }
                    return schema;
                }),
            }),
        onSubmit: (values) => {
            if (values) {
                const data = {
                    cuil: !onboardingWithInvitation
                        ? values?.cuil.replace(new RegExp("[^\\d.,]", "g"), "")
                        : documentData.cuil,
                    role: !onboardingWithInvitation ? values?.companyRole : "",
                    roles: onboardingWithInvitation ? invitationRoles : [],
                    maritalStatus: values?.maritalStatus,
                    occupation: values?.occupation,
                    taxResidence: "Av. Córdoba 1234 5B",
                    currentAddress: true,
                    country: values?.country,
                    province: values?.province,
                    locality: values?.locality,
                    street: values?.street,
                    streetNumber: values?.streetNumber,
                    floor: values?.floor,
                    apartment: values?.apartment,
                    postalCode: values?.postalCode,
                    fatca: values?.fatca,
                    fatcaNumber1: values?.fatcaNumber1,
                    fatcaNumber2: values?.fatcaNumber2,
                    fatcaNumber3: values?.fatcaNumber3,
                    crsOrOcde: values?.crsOrOcde,
                    localResident: values?.isLocalResident === "true",
                    localResidentNumber: values?.localResidentNumber,
                    residentCountry: values?.residentCountry,
                    foreignResidentNumber: values?.foreignResidentNumber,
                    pep: values?.pep,
                    position: values?.isPosition === "true",
                    isPosition: values?.isPosition === "true",
                    positionType: values?.positionType,
                    dateFromPep: values?.isPosition === "true" ? values?.positionRange[0] : "",
                    dateToPep: values?.isPosition === "true" ? values?.positionRange[1] : "",
                    relationType: values?.relationType,
                };
                dispatch(
                    onboardingActions.savePersonalData(
                        data,
                        documentType,
                        documentData?.documentNumber,
                        documentData?.gender,
                        exchangeToken,
                        !!onboardingWithInvitation,
                    ),
                );
            }
        },
    };

    const onChangeCheckbox = (checkBoxId, allFormik, newVal) => {
        if (!newVal && checkBoxId === "fatca") {
            allFormik.setFieldValue("fatcaNumber1", "", false);
            allFormik.setFieldValue("fatcaNumber2", "", false);
            allFormik.setFieldValue("fatcaNumber3", "", false);
            allFormik.setFieldTouched("fatcaNumber1", false, false);
            allFormik.setFieldTouched("fatcaNumber2", false, false);
            allFormik.setFieldTouched("fatcaNumber3", false, false);
        } else if (!newVal && checkBoxId === "crsOrOcde") {
            allFormik.setFieldValue("isLocalResident", "true", false);
            allFormik.setFieldValue("localResidentNumber", "", false);
            allFormik.setFieldValue("residentCountry", "", false);
            allFormik.setFieldValue("foreignResidentNumber", "", false);
            allFormik.setFieldTouched("isLocalResident", false, false);
            allFormik.setFieldTouched("localResidentNumber", false, false);
            allFormik.setFieldTouched("residentCountry", false, false);
            allFormik.setFieldTouched("foreignResidentNumber", false, false);
        } else if (!newVal && checkBoxId === "pep") {
            allFormik.setFieldValue("isPosition", "true", false);
            allFormik.setFieldValue("positionType", "", false);
            allFormik.setFieldValue("positionRange", [null, null], false);
            allFormik.setFieldValue("relationType", "", false);
            allFormik.setFieldTouched("isPosition", false, false);
            allFormik.setFieldTouched("positionType", false, false);
            allFormik.setFieldTouched("positionRange", false, false);
            allFormik.setFieldTouched("relationType", false, false);
        }
    };

    const onChangeRadioBtn = (checkBoxId, allFormik, newVal) => {
        if (checkBoxId === "isLocalResident" && newVal === "false") {
            allFormik.setFieldValue("localResidentNumber", "", false);
            allFormik.setFieldTouched("localResidentNumber", false, false);
        } else if (checkBoxId === "isLocalResident" && newVal === "true") {
            allFormik.setFieldValue("residentCountry", "", false);
            allFormik.setFieldValue("foreignResidentNumber", "", false);
            allFormik.setFieldTouched("residentCountry", false, false);
            allFormik.setFieldTouched("foreignResidentNumber", false, false);
        } else if (checkBoxId === "isPosition" && newVal === "false") {
            allFormik.setFieldValue("positionType", "", false);
            allFormik.setFieldValue("positionRange", [null, null], false);
            allFormik.setFieldTouched("positionType", false, false);
            allFormik.setFieldTouched("positionRange", false, false);
        } else if (checkBoxId === "isPosition" && newVal === "true") {
            allFormik.setFieldValue("relationType", "", false);
            allFormik.setFieldTouched("relationType", false, false);
        }
    };

    return (
        <>
            {isMobile && (
                <ResponsiveHeader ticket id="legalPerson.title" onClickBack={onHeaderBack}>
                    <MobileButtons image={isIOS ? "images/figma/icon/help-white.svg" : "images/figma/icon/help.svg"}>
                        <Help list={helpStepPersonalData} />
                    </MobileButtons>
                </ResponsiveHeader>
            )}
            <MainContainer showLoader={isLoading}>
                {!isMobile && <Head title="onboarding.noUser.title" onBack={isMobile && onHeaderBack} />}
                <Stepper isMobile={isMobile} page={PAGE_ID} />
                <h1 className="text-center" style={{ textAlign: "left" }}>
                    <CustomText text={i18nUtils.get("", "Generales")} />
                </h1>
                <Formik {...formikProps} ref={formRef}>
                    {(allFormik) => (
                        <Form className="above-the-fold">
                            {!documentData.cuil && (
                                <Field component={TextField} idForm={FORM_ID} name="cuil" hidePlaceholder />
                            )}
                            {documentData.cuil && (
                                <Field
                                    component={TextField}
                                    idForm={FORM_ID}
                                    name="cuil"
                                    disabled
                                    placeholder={documentData.cuil}
                                />
                            )}
                            {!onboardingWithInvitation && (
                                <Field
                                    component={Selector}
                                    idForm={FORM_ID}
                                    name="companyRole"
                                    options={companyRoleList || []}
                                    label={`${i18nUtils.get(
                                        "onboarding.personalData.companyRole.label",
                                        "¿Qué rol cumplís en tu compañía?",
                                    )}`}
                                    placeholder={`${i18nUtils.get(
                                        "onboarding.personalData.selectorPlaceholder",
                                        "Elegir una opción",
                                    )}`}
                                />
                            )}
                            {onboardingWithInvitation && (
                                <CustomText
                                    className="data-label"
                                    text={i18nUtils.get("onboarding.personalData.roles.label", "Tu rol en la compañía")}
                                />
                            )}
                            {onboardingWithInvitation && invitationRoles !== null && invitationRoles.length > 0 && (
                                <div className="grid-table box box-primary">
                                    {invitationRoles.map((role) => (
                                        <span>{i18nUtils.get(`onboarding.invitation.roles.${role}`)}</span>
                                    ))}
                                </div>
                            )}
                            <Field
                                component={Selector}
                                idForm={FORM_ID}
                                name="maritalStatus"
                                options={maritalStatusList || []}
                                label={`${i18nUtils.get(
                                    "onboarding.personalData.maritalStatus.label",
                                    "Estado civil",
                                )}`}
                                placeholder={`${i18nUtils.get(
                                    "onboarding.personalData.selectorPlaceholder",
                                    "Elegir una opción",
                                )}`}
                            />
                            <Field
                                component={Selector}
                                idForm={FORM_ID}
                                name="occupation"
                                options={occupationList || []}
                                label={`${i18nUtils.get(
                                    "onboarding.personalData.occupation.label",
                                    "Actividad / ocupación",
                                )}`}
                                placeholder={`${i18nUtils.get(
                                    "onboarding.personalData.selectorPlaceholder",
                                    "Elegir una opción",
                                )}`}
                            />
                            <CustomText className="data-label" text={i18nUtils.get("", "Domicilio")} />

                            {address !== null && address !== "" && (
                                <div className="grid-table box box-primary">
                                    <span>{address}</span>
                                </div>
                            )}
                            <Field
                                component={Selector}
                                idForm={FORM_ID}
                                name="country"
                                options={countryList || [{ value: "1", label: "Argentina" }]}
                                label={`${i18nUtils.get("onboarding.personalData.country.label", "País")}`}
                                // placeholder={`${i18nUtils.get(
                                //     "onboarding.personalData.selectorPlaceholder",
                                //     "Elegir una opción",
                                // )}`}
                                placeholder="Argentina"
                                disabled
                            />
                            <Field
                                component={Selector}
                                idForm={FORM_ID}
                                name="province"
                                onChange={onChangeProvince}
                                options={stateList}
                                label={`${i18nUtils.get("onboarding.personalData.province.label", "Provincia")}`}
                                placeholder={`${i18nUtils.get(
                                    "onboarding.personalData.selectorPlaceholder",
                                    "Elegir una opción",
                                )}`}
                            />
                            <Field
                                component={Selector}
                                idForm={FORM_ID}
                                name="locality"
                                options={localityList}
                                label={`${i18nUtils.get("onboarding.personalData.locality.label", "Localidad")}`}
                                placeholder={`${i18nUtils.get(
                                    "onboarding.personalData.selectorPlaceholder",
                                    "Elegir una opción",
                                )}`}
                                disabled={allFormik?.values?.province !== "" && fetching}
                            />
                            <Field
                                component={TextField}
                                idForm={FORM_ID}
                                name="street"
                                labelTextDefault={`${i18nUtils.get("onboarding.personalData.street.label", "Calle")}`}
                                hidePlaceholder
                                hasRegex="^[A-Za-z0-9\-_\. ]*$"
                            />
                            <Field
                                component={TextField}
                                idForm={FORM_ID}
                                name="streetNumber"
                                labelTextDefault={`${i18nUtils.get(
                                    "onboarding.personalData.streetNumber.label",
                                    "Número",
                                )}`}
                                hidePlaceholder
                                onlyNumbers
                            />
                            <Field
                                component={TextField}
                                idForm={FORM_ID}
                                name="floor"
                                labelTextDefault={`${i18nUtils.get("onboarding.personalData.floor.label", "Piso")}`}
                                hidePlaceholder
                                onlyNumbers
                                maxLength={2}
                            />
                            <Field
                                component={TextField}
                                idForm={FORM_ID}
                                name="apartment"
                                labelTextDefault={`${i18nUtils.get(
                                    "onboarding.personalData.apartment.label",
                                    "Departamento",
                                )}`}
                                hidePlaceholder
                                hasRegex="^[A-Za-z0-9\-_\. ]*$"
                                maxLength={3}
                            />
                            <Field
                                component={TextField}
                                idForm={FORM_ID}
                                name="postalCode"
                                labelTextDefault={`${i18nUtils.get(
                                    "onboarding.personalData.postalCode.label",
                                    "Código postal",
                                )}`}
                                hidePlaceholder
                                onlyNumbers
                            />
                            <I18n
                                id="onboarding.regulatoryData.title"
                                defaultValue="Datos adicionales"
                                component="h1"
                            />
                            <ul className="list">
                                <li>
                                    <Field
                                        component={Checkbox}
                                        idForm={FORM_ID}
                                        name="fatca"
                                        labelText="onboarding.regulatoryData.fatca.label"
                                        labelTextProps={{
                                            defaultValue: "¿Sos residente fiscal en Estados Unidos (FATCA)?",
                                        }}
                                        onChange={(v) => onChangeCheckbox("fatca", allFormik, v)}
                                    />
                                    {!!allFormik?.values?.fatca && (
                                        <>
                                            <p>
                                                <I18n
                                                    id="onboarding.regulatoryData.fatcaNumber.label"
                                                    defaultValue="Número de seguro social (SSN)"
                                                />
                                            </p>
                                            <div className="grid grid-cols-3 grid-rows-1 gap-x-3 items-start">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="fatcaNumber1"
                                                    hideLabel
                                                    maxLength="3"
                                                    placeholder={`${i18nUtils.get(
                                                        "onboarding.regulatoryData.fatcaNumber1.placeholder",
                                                        "XXX",
                                                    )}`}
                                                    hintLabel={`${i18nUtils.get(
                                                        "onboarding.regulatoryData.fatcaNumber1.hintLabel",
                                                        "3 dígitos.",
                                                    )}`}
                                                    autoFocus={!isMobile}
                                                />
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="fatcaNumber2"
                                                    hideLabel
                                                    maxLength="2"
                                                    placeholder={`${i18nUtils.get(
                                                        "onboarding.regulatoryData.fatcaNumber2.placeholder",
                                                        "XX",
                                                    )}`}
                                                    hintLabel={`${i18nUtils.get(
                                                        "onboarding.regulatoryData.fatcaNumber2.hintLabel",
                                                        "2 dígitos.",
                                                    )}`}
                                                />
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="fatcaNumber3"
                                                    hideLabel
                                                    maxLength="4"
                                                    placeholder={`${i18nUtils.get(
                                                        "onboarding.regulatoryData.fatcaNumber3.placeholder",
                                                        "XXXX",
                                                    )}`}
                                                    hintLabel={`${i18nUtils.get(
                                                        "onboarding.regulatoryData.fatcaNumber3.hintLabel",
                                                        "4 dígitos.",
                                                    )}`}
                                                />
                                            </div>
                                        </>
                                    )}
                                </li>
                                <li>
                                    <Field
                                        component={Checkbox}
                                        idForm={FORM_ID}
                                        name="crsOrOcde"
                                        labelText="onboarding.regulatoryData.crsOrOcde.label"
                                        labelTextProps={{
                                            defaultValue:
                                                "¿Sos residente fiscal en otro país distinto a Estados Unidos (CRS/OCDE)?",
                                        }}
                                        onChange={(v) => onChangeCheckbox("crsOrOcde", allFormik, v)}
                                    />
                                    {!!allFormik?.values?.crsOrOcde && (
                                        <>
                                            <Field
                                                component={Selector}
                                                renderAs="radio"
                                                idForm={FORM_ID}
                                                name="isLocalResident"
                                                onChange={(v) => onChangeRadioBtn("isLocalResident", allFormik, v)}
                                                options={[
                                                    {
                                                        id: "true",
                                                        label: i18nUtils.get(
                                                            "onboarding.regulatoryData.isLocalResident.true.label",
                                                            "Residente Local",
                                                        ),
                                                    },
                                                    {
                                                        id: "false",
                                                        label: i18nUtils.get(
                                                            "onboarding.regulatoryData.isLocalResident.false.label",
                                                            "Residente Exterior",
                                                        ),
                                                    },
                                                ]}
                                            />
                                            {allFormik?.values?.isLocalResident === "true" ? (
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="localResidentNumber"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.regulatoryData.localResidentNumber.label",
                                                        "CUIL / CUIT",
                                                    )}`}
                                                    placeholder={`${i18nUtils.get(
                                                        "onboarding.regulatoryData.localResidentNumber.placeholder",
                                                        "ej: 30403333334",
                                                    )}`}
                                                    hintLabel={`${i18nUtils.get(
                                                        "onboarding.regulatoryData.localResidentNumber.hintLabel",
                                                        "Debe contener 11 caracteres.",
                                                    )}`}
                                                    autoFocus={!isMobile}
                                                />
                                            ) : (
                                                <>
                                                    <Field
                                                        component={Selector}
                                                        idForm={FORM_ID}
                                                        name="residentCountry"
                                                        options={foreignCountries || []}
                                                        label={`${i18nUtils.get(
                                                            "onboarding.regulatoryData.residentCountry.label",
                                                            "País",
                                                        )}`}
                                                        placeholder={`${i18nUtils.get(
                                                            "onboarding.personalData.selectorPlaceholder",
                                                            "Elegir una opción",
                                                        )}`}
                                                    />
                                                    <Field
                                                        component={TextField}
                                                        idForm={FORM_ID}
                                                        name="foreignResidentNumber"
                                                        labelTextDefault={`${i18nUtils.get(
                                                            "onboarding.regulatoryData.foreignResidentNumber.label",
                                                            "NIF",
                                                        )}`}
                                                        placeholder={`${i18nUtils.get(
                                                            "onboarding.regulatoryData.fatcaNumber1.placeholder",
                                                            "ej: 403333333",
                                                        )}`}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}
                                </li>
                                <li>
                                    <Field
                                        component={Checkbox}
                                        idForm={FORM_ID}
                                        name="pep"
                                        labelText="onboarding.regulatoryData.pep.label"
                                        labelTextProps={{ defaultValue: "¿Soy Persona Expuesta Políticamente (PEP)?" }}
                                        onChange={(v) => onChangeCheckbox("pep", allFormik, v)}
                                    />
                                    {!!allFormik?.values?.pep && (
                                        <>
                                            <Field
                                                component={Selector}
                                                renderAs="radio"
                                                idForm={FORM_ID}
                                                name="isPosition"
                                                onChange={(v) => onChangeRadioBtn("isPosition", allFormik, v)}
                                                options={[
                                                    {
                                                        id: "true",
                                                        label: i18nUtils.get(
                                                            "onboarding.regulatoryData.isPosition.true.label",
                                                            "Cargo",
                                                        ),
                                                    },
                                                    {
                                                        id: "false",
                                                        label: i18nUtils.get(
                                                            "onboarding.regulatoryData.isPosition.false.label",
                                                            "Relación",
                                                        ),
                                                    },
                                                ]}
                                            />
                                            {allFormik?.values?.isPosition === "true" ? (
                                                <>
                                                    <Field
                                                        component={Selector}
                                                        idForm={FORM_ID}
                                                        name="positionType"
                                                        options={pepJobsList || []}
                                                        label={`${i18nUtils.get(
                                                            "onboarding.regulatoryData.positionType.label",
                                                            "Tipo de Cargo",
                                                        )}`}
                                                        placeholder={`${i18nUtils.get(
                                                            "onboarding.personalData.selectorPlaceholder",
                                                            "Elegir una opción",
                                                        )}`}
                                                    />
                                                    <Field
                                                        component={DateRangeField}
                                                        idForm={FORM_ID}
                                                        defaultLabel="Fecha fin de cargo PEP"
                                                        minDate={getLimitDate(-3650)}
                                                        maxDate={getLimitDate(3650)}
                                                        name="positionRange"
                                                        dateFormat="dd/MM/yyyy"
                                                        selectsRange
                                                        showMonthYearDropdown
                                                        scrollableMonthYearDropdown
                                                        popperPlacement="bottom"
                                                        showGlobalPlaceholder
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <Field
                                                        component={Selector}
                                                        idForm={FORM_ID}
                                                        name="relationType"
                                                        options={pepRelationshipTypesList || []}
                                                        label={`${i18nUtils.get(
                                                            "onboarding.regulatoryData.relationType.label",
                                                            "Tipo de Relación",
                                                        )}`}
                                                        placeholder={`${i18nUtils.get(
                                                            "onboarding.personalData.selectorPlaceholder",
                                                            "Elegir una opción",
                                                        )}`}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}
                                </li>
                            </ul>

                            {!allFormik?.values?.fatca && !allFormik?.values?.crsOrOcde && !allFormik?.values?.pep && (
                                <Col
                                    xs={12}
                                    md={8}
                                    mdOffset={2}
                                    className="box box-border-soft"
                                    style={{ padding: "20px" }}>
                                    <CustomText
                                        text={i18nUtils.get(
                                            "",
                                            "En el caso que no te corresponda ninguno de estos datos, podés continuar.",
                                        )}
                                    />
                                </Col>
                            )}
                            <div className="form-actions">
                                <Button
                                    bsStyle="primary"
                                    label="global.continue"
                                    loading={personalDataStatus === "FETCHING"}
                                    type="submit"
                                    block
                                    disabled={!submitIsAllowed(allFormik)}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </MainContainer>

            <ErrorPopup error={error} handleClick={() => dispatch(modalActions.modalClose())} />
        </>
    );
};

StepPersonalData.propTypes = {
    isMobile: bool.isRequired,
    address: string.isRequired,
    companyRoleList: arrayOf(shape({})).isRequired,
    maritalStatusList: arrayOf(shape({})).isRequired,
    occupationList: arrayOf(shape({})).isRequired,
    countryList: arrayOf(shape({})).isRequired,
    foreignCountries: arrayOf(shape({})).isRequired,
    stateList: arrayOf(shape({})).isRequired,
    localityList: arrayOf(shape({})).isRequired,
    documentData: shape({}).isRequired,
    documentType: string.isRequired,
    onboardingWithInvitation: bool.isRequired,
    invitationRoles: arrayOf(shape({})).isRequired,
    exchangeToken: string.isRequired,
    personalDataStatus: string.isRequired,
    fetching: bool.isRequired,
    pepJobsList: arrayOf(shape({})).isRequired,
    pepRelationshipTypesList: arrayOf(shape({})).isRequired,
    compliance: shape({}).isRequired,
};

const mapStateToProps = (state) => ({
    address: onboardingSelectors.getDocumentAddress(state),
    companyRoleList: onboardingSelectors.getCompanyRoleList(state),
    maritalStatusList: onboardingSelectors.getMaritalStatusList(state),
    occupationList: onboardingSelectors.getOccupationList(state),
    countryList: onboardingSelectors.getCountryList(state),
    stateList: onboardingSelectors.getStateList(state),
    localityList: onboardingSelectors.getLocalityList(state),
    foreignCountries: onboardingSelectors.getForeignCountries(state),
    fetching: onboardingSelectors.getFetching(state),

    onboardingWithInvitation: onboardingSelectors.getOnboardingWithInvitation(state),
    invitationRoles: onboardingSelectors.getInvitationData(state)?.roles,
    exchangeToken: enrollmentSelectors.getExchangeToken(state),
    personalDataStatus: onboardingSelectors.getPersonalDataStatus(state),

    documentData: onboardingSelectors.getDocumentData(state),
    documentType: onboardingSelectors.getDocumentType(state),

    pepJobsList: onboardingSelectors.getPepJobsList(state),
    pepRelationshipTypesList: onboardingSelectors.getPepRelationshipTypesList(state),
    compliance: onboardingSelectors.getInvitationData(state)?.compliance,
});

export default connect(mapStateToProps)(StepPersonalData);
